import { GOOGLE_ADDRESS_VALIDATION_URL } from "@/lib/constants";

interface GoogleAddressValidationResponse {
  result: {
    verdict: {
      hasUnconfirmedComponents?: boolean;
      hasReplacedComponents?: boolean;
      addressComplete?: boolean;
    };
    address: {
      addressComponents: Array<{
        componentType: string;
        confirmationLevel: string;
        replaced?: boolean;
        componentName: {
          text: string;
        };
      }>;
    };
  };
}

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

interface AddressData {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postal_code: string;
}

export async function validateUsZipCode(
  addressData: AddressData,
): Promise<ValidationResult> {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return { isValid: true };
  }

  const addressLines = [addressData.address1];
  if (addressData.address2) {
    addressLines.push(addressData.address2);
  }

  try {
    const response = await fetch(
      `${GOOGLE_ADDRESS_VALIDATION_URL}?key=${apiKey}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          address: {
            regionCode: "US",
            addressLines,
            locality: addressData.city,
            administrativeArea: addressData.state,
            postalCode: addressData.postal_code,
          },
        }),
      },
    );

    if (!response.ok) {
      console.error("Google Address Validation API error:", response.status);
      return { isValid: true };
    }

    const data: GoogleAddressValidationResponse = await response.json();

    const postalCodeComponent = data.result?.address?.addressComponents?.find(
      (component) => component.componentType === "postal_code",
    );

    const stateComponent = data.result?.address?.addressComponents?.find(
      (component) => component.componentType === "administrative_area_level_1",
    );

    const isPostalCodeReplaced = postalCodeComponent?.replaced === true;

    const isStateReplaced = stateComponent?.replaced === true;

    const isPostalCodeUnconfirmed =
      postalCodeComponent?.confirmationLevel === "UNCONFIRMED_BUT_PLAUSIBLE" ||
      postalCodeComponent?.confirmationLevel === "UNCONFIRMED_AND_SUSPICIOUS";

    const isStateUnconfirmed =
      stateComponent?.confirmationLevel === "UNCONFIRMED_BUT_PLAUSIBLE" ||
      stateComponent?.confirmationLevel === "UNCONFIRMED_AND_SUSPICIOUS";

    const returnedPostalCode = postalCodeComponent?.componentName?.text;
    const normalizeZip = (zip: string) => zip.replace(/[\s-]/g, "");
    const isPostalCodeChanged =
      returnedPostalCode &&
      addressData.postal_code &&
      normalizeZip(returnedPostalCode) !==
        normalizeZip(addressData.postal_code);

    if (
      isPostalCodeReplaced ||
      isStateReplaced ||
      isPostalCodeUnconfirmed ||
      isStateUnconfirmed ||
      isPostalCodeChanged
    ) {
      return {
        isValid: false,
        error: "zip_code_invalid_for_state",
      };
    }

    return { isValid: true };
  } catch (error) {
    console.error("Google Address Validation API error:", error);
    return { isValid: true };
  }
}
